import { useEffect, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import NoSleep from 'nosleep.js';

function isIOSDevice(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export function useScreenWakeLock() {
  const toast = useToast();
  const [isActive, setIsActive] = useState(false);
  const noSleepRef = useRef<NoSleep | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null as unknown as WakeLockSentinel | null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize wake lock based on platform
  useEffect(() => {
    if (isIOSDevice()) {
      const supportsNativeWakeLock = typeof (navigator as any).wakeLock !== 'undefined';
      let usingFallback = false;

      // Fallback: canvas video + silent audio to keep iOS awake
      const activateFallback = () => {
        if (usingFallback) return;
        usingFallback = true;

        // Create hidden video element for iOS
        const video = document.createElement('video');
        video.playsInline = true;
        video.muted = true;
        video.loop = true;
        video.autoplay = true;
        video.controls = false;
        video.style.width = '1px';
        video.style.height = '1px';
        video.style.position = 'fixed';
        video.style.top = '0';
        video.style.left = '0';
        video.style.opacity = '0.01';

        // Create a simple canvas and record it as a video stream
        const canvas = document.createElement('canvas');
        canvas.width = 16;
        canvas.height = 16;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#000000';
          ctx.fillRect(0, 0, 16, 16);
        }

        // Create a video stream from the canvas
        const stream = canvas.captureStream(1);
        video.srcObject = stream;

        // Also create an audio element playing silence
        const audio = document.createElement('audio');
        audio.setAttribute('playsinline', 'true');
        audio.setAttribute('webkit-playsinline', 'true');
        audio.src = 'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjIwLjEwMAAAAAAAAAAAAAAA//tUAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAAUAAAogAAiIiIiIiIiIiIiIjw8PDw8PDw8PDw8VVVVVVVVVVVVVVVvb29vb29vb29vb4iIiIiIiIiIiIiInJycnJycnJycnJy1tbW1tbW1tbW1tc7Ozs7Ozs7Ozs7O2tra2tra2tra2tr09PT09PT09PT09P////////8AAAAATGF2YzU4LjM1AAAAAAAAAAAAAAAAJAYAAAAAAAAAKIBk7/2kAAAAAAAAAAAAAAAAAAAA//sUZAAP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sUZB4P8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sUZDwP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sUZFoP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sUZHgP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sUZJYP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sUZLQP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sUZMoP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';
        audio.loop = true;

        // Add elements to the document
        document.body.appendChild(video);
        document.body.appendChild(audio);

        // Store refs
        videoRef.current = video;
        audioRef.current = audio;

        const startMedia = async () => {
          try {
            await Promise.all([video.play(), audio.play()]);
            setIsActive(true);
          } catch (err) {
            // Wait for user gesture
          }
        };

        const handleUserInteraction = async () => {
          if (!isActive) {
            await startMedia();
          }
        };

        document.addEventListener('touchstart', handleUserInteraction, { once: true });
        document.addEventListener('click', handleUserInteraction, { once: true });

        // Attempt immediate start
        startMedia();
      };

      // Native Screen Wake Lock flow for iOS Safari (if supported)
      const requestNativeWakeLock = async () => {
        try {
          // Some Safari versions require a user gesture; we attempt anyway and also on interaction
          const sentinel = await (navigator as any).wakeLock.request('screen');
          wakeLockRef.current = sentinel;
          setIsActive(true);
          sentinel.addEventListener('release', () => {
            setIsActive(false);
          });
        } catch (err: any) {
          // On failure, fall back to media approach
          if (!usingFallback) activateFallback();
        }
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible' && !wakeLockRef.current && !usingFallback) {
          requestNativeWakeLock();
        }
      };

      const handleUserGesture = () => {
        if (!usingFallback && !wakeLockRef.current) {
          requestNativeWakeLock();
        }
      };

      if (supportsNativeWakeLock) {
        // Try requesting immediately and on visibility/user gesture
        requestNativeWakeLock();
        document.addEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener('click', handleUserGesture, { once: true });
        document.addEventListener('touchend', handleUserGesture, { once: true });
      } else {
        // Fallback for older iOS Safari
        activateFallback();
      }

      return () => {
        // Cleanup native wake lock
        if (wakeLockRef.current) {
          try {
            wakeLockRef.current.release();
          } catch (_) {
            // ignore
          }
          wakeLockRef.current = null;
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        document.removeEventListener('click', handleUserGesture as any);
        document.removeEventListener('touchend', handleUserGesture as any);

        // Cleanup fallback media if used
        if (videoRef.current) {
          try { videoRef.current.pause(); } catch {}
          videoRef.current.remove();
          videoRef.current = null;
        }
        if (audioRef.current) {
          try { audioRef.current.pause(); } catch {}
          audioRef.current.remove();
          audioRef.current = null;
        }
      };
    } else {
      // Use NoSleep.js for non-iOS devices
      noSleepRef.current = new NoSleep();
      
      const enableWakeLock = async () => {
        try {
          if (noSleepRef.current && !isActive) {
            await noSleepRef.current.enable();
            setIsActive(true);
          }
        } catch (err) {
          console.error('Wake lock failed:', err);
        }
      };

      // Enable on mount
      enableWakeLock();

      // Enable on user interaction
      document.addEventListener('click', enableWakeLock, { once: true });

      return () => {
        if (noSleepRef.current) {
          noSleepRef.current.disable();
        }
      };
    }
  }, []);

  return { isActive };
}